import React, { FunctionComponent, useState } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { Navigate, useNavigate } from 'react-router-dom';
import { generateStoreNumbers } from 'src/components/pages/salesChannels/helpers';
import { T, backofficeUrl, getUrlWithQueryParams, toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { Badge } from '../../../../widgets/badge';
import { Link, PrimaryButton } from '../../../../widgets/buttons';
import { FormControl } from '../../../../widgets/formControl';
import { NotificationBanner } from '../../../../widgets/notificationBanner/notificationBanner';
import { AdvancedSelect } from '../../../../widgets/select';
import { Tab, TabList, TabPanel, ZTabs } from '../../../../widgets/ztabs';
import {
    editProductNameAction,
    modeAction,
    newProductNoIntegrationAction,
    setSalesChannelsAction,
    useProductConfigurationChangeStep,
    useProductConfigurationState
} from '../../state';
import pages from '../../state/pages';
import { addProductQuery } from './__generated__/addProductQuery.graphql';

// --- Layout ---

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;
    max-width: 920px;
    margin: 0 auto;
    padding: 24px 24px 80px;
`;

const PageHeader = styled.div`
    text-align: center;
`;

const PageTitle = styled.h1`
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: ${(props) => props.theme.app.textColor};
    font-family: inherit;
`;

const PageSubtitle = styled.p`
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
`;

const CenteredTabs = styled(ZTabs)<{ $fewTabs?: boolean }>`
    .react-tabs__tab-list {
        display: flex;
        width: 100%;
        margin: 0 auto 16px;
        /* With few tabs, keep the tab row and the panel below the same (narrower) centered width. */
        ${(props) => props.$fewTabs && 'max-width: 520px;'}
    }

    .react-tabs__tab {
        flex: 1 1 0%;
        min-width: 0;
        justify-content: center;
        padding-top: 14px;
        padding-bottom: 14px;
    }

    .react-tabs__tab-panel--selected {
        display: flex;
        flex-direction: column;
        /* Reserve a consistent height so the FAQ below stays in a fixed position when switching tabs. */
        min-height: 320px;
    }
`;

// --- Tab description box ---

const descriptionBoxStyles = {
    store: { background: '#eff6ff', border: '#bfdbfe', color: '#1e40af' },
    standalone: { background: '#fdf4ff', border: '#f0abfc', color: '#86198f' },
    pod: { background: '#f0fdf4', border: '#86efac', color: '#166534' }
};

const TabDescriptionBox = styled.div<{ $variant: 'store' | 'standalone' | 'pod' }>`
    background: ${(props) => descriptionBoxStyles[props.$variant].background};
    border: 1px solid ${(props) => descriptionBoxStyles[props.$variant].border};
    border-radius: 10px;
    padding: 16px 20px;
    margin-bottom: 10px;
    font-size: 13px;
    line-height: 1.6;
    min-height: 90px;
    display: flex;
    align-items: center;
    color: ${(props) => descriptionBoxStyles[props.$variant].color};
`;

// --- Source cards ---

const ChannelLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
`;

const ContinueButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 24px;
`;

const ContinueButton = styled(PrimaryButton).attrs({ $size: 'large' as const })`
    min-width: 280px;
    justify-content: center;
`;

const StandaloneButton = styled(PrimaryButton).attrs({ $size: 'large' as const })`
    min-width: 280px;
    justify-content: center;
    background: #f97316;
    border-color: #f97316;

    &:hover {
        background: #ea580c;
        border-color: #ea580c;
    }
`;

const PodWarningBanner = styled(NotificationBanner)`
    margin-bottom: 28px;
`;

const PodMismatchBanner = styled(NotificationBanner)`
    margin-top: 12px;
`;

// --- No store empty state ---

const NoStoreContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px 24px 32px;
    text-align: center;
`;

const NoStoreTitle = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const NoStoreDescription = styled.span`
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
    max-width: 400px;
`;

// --- FAQ ---

const FaqSection = styled.div``;

const FaqTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 16px 0;
`;

const FaqList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 10px;
    overflow: hidden;
`;

const FaqItemContainer = styled.div`
    &:not(:last-child) {
        border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    }
`;

const FaqQuestion = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 14px 20px;
    background: #f9fafb;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    font-family: inherit;
    text-align: left;
    line-height: 1.4;

    &:hover {
        background: #f3f4f6;
    }
`;

const FaqChevron = styled.span<{ $open?: boolean }>`
    font-size: 16px;
    color: #9ca3af;
    transition: transform 0.2s;
    transform: ${(props) => (props.$open ? 'rotate(180deg)' : 'rotate(0)')};
    flex-shrink: 0;
    margin-left: 12px;
`;

const FaqAnswer = styled.div<{ $open?: boolean }>`
    padding: ${(props) => (props.$open ? '0 20px 14px' : '0 20px')};
    max-height: ${(props) => (props.$open ? '400px' : '0')};
    overflow: hidden;
    transition:
        max-height 0.2s ease,
        padding 0.2s ease;
    font-size: 13px;
    color: #6b7280;
    line-height: 1.6;
    background: #f9fafb;
`;

const FaqItem = ({ question, answer, isOpen, onToggle }: { question: string; answer: React.ReactNode; isOpen: boolean; onToggle: () => void }) => {
    return (
        <FaqItemContainer>
            <FaqQuestion onClick={onToggle}>
                {question}
                <FaqChevron $open={isOpen}>&#9662;</FaqChevron>
            </FaqQuestion>
            <FaqAnswer $open={isOpen}>{answer}</FaqAnswer>
        </FaqItemContainer>
    );
};

// --- Component ---

const AddProduct: FunctionComponent<{}> = () => {
    const { state, dispatch } = useProductConfigurationState();
    const changeStep = useProductConfigurationChangeStep();
    const navigate = useNavigate();

    const query = useLazyLoadQuery<addProductQuery>(
        graphql`
            query addProductQuery {
                modelsCount
                ecommerceSettings {
                    ecommerceName
                    hasProductsApi
                }
                salesChannels(status: ACTIVE) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        ecommerceName
                        remoteUrl
                        hasProductsApi
                        definitiveUrl
                    }
                    supportedProviders
                }
            }
        `,
        {}
    );

    const realSalesChannels = query.salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
    const noEcommerceSalesChannel = query.salesChannels.find((s) => s.ecommerceSettings?.ecommerceName === 'NONE');

    const salesChannelIDs = realSalesChannels.map((s) => s.id);
    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    const [selectedChannelId, setSelectedChannelId] = useState<string>(realSalesChannels.length > 0 ? realSalesChannels[0].id : '');
    const selectedChannel = realSalesChannels.find((s) => s.id === selectedChannelId) ?? realSalesChannels[0];

    const [activeTab, setActiveTab] = useState(0);
    const [openFaq, setOpenFaq] = useState<string | null>(null);
    const [podSelectedChannelId, setPodSelectedChannelId] = useState<string>(realSalesChannels.length > 0 ? realSalesChannels[0].id : '');
    const podSelectedChannel = realSalesChannels.find((s) => s.id === podSelectedChannelId) ?? realSalesChannels[0];

    const masterEcommerce = query.ecommerceSettings?.ecommerceName || '';
    const podSupportedPlatforms = ['SHOPIFY', 'WOO_COMMERCE', 'BIG_COMMERCE'];
    const showPodTab = masterEcommerce === 'NONE' || podSupportedPlatforms.includes(masterEcommerce);
    const visibleTabCount = 1 + (noEcommerceSalesChannel ? 1 : 0) + (showPodTab ? 1 : 0);
    const masterChannelId = realSalesChannels.find((s) => toLocalID(s.id) === 1)?.id;
    const isPodAvailableForSelected = masterEcommerce === 'NONE' || podSelectedChannel?.id === masterChannelId;

    const getChannelLabel = (salesChannel: (typeof query)['salesChannels'][0]) => {
        const storeNumber = storeNumbers.find((x) => x.salesChannelID === salesChannel.id)?.storeNumber || 1;
        const name = salesChannel.detail?.properties?.name || '';
        const ecommerce = salesChannel.ecommerceSettings?.ecommerceName || '';
        const parts = [`Store ${storeNumber}`];
        if (name) parts.push(name);
        if (ecommerce && ecommerce !== 'NONE') parts.push(`(${ecommerce.replace(/_/g, ' ')})`);
        if (salesChannel.id === masterChannelId) parts.push(`— ${T._('Master', 'Admin')}`);
        return parts.join(' ');
    };

    // Single-channel auto-redirect
    if (
        query.salesChannels.length == 1 &&
        realSalesChannels.length == 1 &&
        realSalesChannels[0].supportedProviders.length === 0 &&
        realSalesChannels[0].ecommerceSettings?.ecommerceName != 'NONE'
    ) {
        if (query.ecommerceSettings?.ecommerceName === 'API' && !query.ecommerceSettings?.hasProductsApi) {
            return <Navigate to={backofficeUrl('/tools/bulk-import')} replace />;
        } else {
            dispatch(setSalesChannelsAction(realSalesChannels[0].id, [realSalesChannels[0].ecommerceSettings!.ecommerceName]));
            dispatch(modeAction('classic'));
            return <Navigate to={getUrlWithQueryParams(pages.find((x) => x.key === 'connect-catalog')!.url)} />;
        }
    }

    const scratchTabIndex = noEcommerceSalesChannel ? 1 : -1;
    const podTabIndex = noEcommerceSalesChannel ? 2 : 1;

    const handleContinue = () => {
        if (activeTab === 0 && selectedChannel) {
            // From Store
            dispatch(setSalesChannelsAction(selectedChannel.id, [selectedChannel.ecommerceSettings!.ecommerceName]));
            dispatch(modeAction('classic'));
            changeStep('connect-catalog');
        } else if (activeTab === scratchTabIndex && noEcommerceSalesChannel) {
            // From Scratch
            dispatch(setSalesChannelsAction(noEcommerceSalesChannel.id, [noEcommerceSalesChannel.ecommerceSettings!.ecommerceName]));
            dispatch(modeAction('classic'));
            dispatch(newProductNoIntegrationAction());
            dispatch(editProductNameAction(`Product ${(query.modelsCount ?? 0) + 1}`));
            changeStep('print-area');
        } else if (activeTab === podTabIndex && podSelectedChannel) {
            // POD
            dispatch(setSalesChannelsAction(podSelectedChannel.id, [podSelectedChannel.ecommerceSettings!.ecommerceName]));
            dispatch(modeAction('pod'));
            changeStep('supplier-selection');
        }
    };

    const getEcommerceName = () => {
        if (!selectedChannel) return '';
        return selectedChannel.ecommerceSettings?.ecommerceName?.replace(/_/g, ' ') || '';
    };

    const faqs = [
        {
            question: T._('What is a customizable product?', 'Admin'),
            answer: (
                <>
                    {T._(
                        'In Zakeke, a customizable product works like a configuration template that you link to an existing product in your store to make it customizable. You are not creating a new product inside Zakeke; instead, you create a configuration that transforms an existing store product into a customizable one.',
                        'Admin'
                    )}
                    <br />
                    <br />
                    {T._(
                        'It is also possible to create a product entirely from scratch. This creates a standalone product that you can either link to a product in your store later to make it customizable or use independently for non-eCommerce workflows, such as processing manual orders by sending the customizer link directly to your customers, creating Zakeke-hosted mini-sites, or running tests and demos.',
                        'Admin'
                    )}
                </>
            )
        },
        {
            question: T._('What is the difference between "From Store" and "From Scratch"?', 'Admin'),
            answer: T._(
                '"From Store" imports an existing product from your connected e-commerce, syncing SKU, images, and variants automatically. "From Scratch" lets you create a standalone product without any store connection — you can link it later.',
                'Admin'
            )
        },
        ...(showPodTab
            ? [
                  {
                      question: T._('What is a Print on Demand (POD) service and how does it work with Zakeke?', 'Admin'),
                      answer: (
                          <>
                              {T._(
                                  'Print on Demand (POD) is a service model that allows you to sell printed products without managing inventory or production. Providers such as Printful, CustomCat, and Printeers are integrated with Zakeke and supply product catalogs that can be published directly on your store. When a customer personalizes a product and places an order, the final design is automatically sent to the POD provider, who takes care of printing, packaging, and shipping the product directly to the end customer. It is also possible to use POD providers that are not natively integrated with Zakeke via custom integration. More information about POD',
                                  'Admin'
                              )}{' '}
                              <Link href='https://zakeke.zendesk.com/hc/en-us/sections/360004775860' target='_blank'>
                                  {T._('here', 'Admin')}
                              </Link>
                              {T._('.', 'Admin')}
                          </>
                      )
                  }
              ]
            : []),
        {
            question: T._('Can I connect my store later if I start from scratch?', 'Admin'),
            answer: T._('Yes, you can create a standalone product first and connect it to your store at any time afterwards.', 'Admin')
        },
        {
            question: T._('Which e-commerce platforms are supported?', 'Admin'),
            answer: (
                <>
                    {T._(
                        'Zakeke offers a no-code connector for major e-commerce platforms such as Shopify, WooCommerce, BigCommerce, and more. Full list',
                        'Admin'
                    )}{' '}
                    <Link href='https://zakeke.zendesk.com/hc/en-us/articles/360013971659-Supported-eCommerce-Platforms' target='_blank'>
                        {T._('here', 'Admin')}
                    </Link>
                    {T._(
                        '. In addition, APIs are available to integrate Zakeke with any other e-commerce platform that does not have a native connector or for custom integrations.',
                        'Admin'
                    )}
                </>
            )
        },
        {
            question: T._('What types of products can I customize?', 'Admin'),
            answer: (
                <>
                    {T._(
                        'Zakeke is product-agnostic, meaning it can be used to customize virtually any type of decorated product, including printed, engraved, embroidered, or otherwise personalized items.',
                        'Admin'
                    )}
                    <br />
                    <br />
                    {T._('You can find a general overview guide', 'Admin')}{' '}
                    <Link href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693-Create-a-customizable-product' target='_blank'>
                        {T._('here', 'Admin')}
                    </Link>
                    {T._(', along with additional guides for specific use cases', 'Admin')}{' '}
                    <Link href='https://zakeke.zendesk.com/hc/en-us/sections/360004921479-How-to' target='_blank'>
                        {T._('here', 'Admin')}
                    </Link>
                    {T._('.', 'Admin')}
                </>
            )
        }
    ];

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{T._('Define your product source and type', 'Admin')}</PageTitle>
                <PageSubtitle>
                    {T._('Choose your starting point to create a customizable product.', 'Admin')}{' '}
                    <Link href='https://zakeke.zendesk.com/hc/en-us/articles/360026668693' target='_blank'>
                        {T._('Learn more', 'Admin')}
                    </Link>
                </PageSubtitle>
            </PageHeader>

            <CenteredTabs selectedIndex={activeTab} onSelect={setActiveTab} $fewTabs={visibleTabCount <= 2}>
                <TabList>
                    <Tab>
                        {T._('From Store', 'Admin')} <Badge variant='store'>{T._('Store product', 'Admin')}</Badge>
                    </Tab>
                    {noEcommerceSalesChannel && (
                        <Tab>
                            {T._('From Scratch', 'Admin')} <Badge variant='standalone'>{T._('Standalone product', 'Admin')}</Badge>
                        </Tab>
                    )}
                    {showPodTab && (
                        <Tab>
                            {T._('From POD Catalogs', 'Admin')} <Badge variant='pod'>{T._('POD Product', 'Admin')}</Badge>
                        </Tab>
                    )}
                </TabList>

                {/* From Store */}
                <TabPanel>
                    <TabDescriptionBox $variant='store'>
                        {T._(
                            'Import a product from your store and turn it into a customizable product via Zakeke. Visitors at your store will be able to access the customizer for that product directly on your website, personalize on their own and purchase. Ideal for online stores.',
                            'Admin'
                        )}
                    </TabDescriptionBox>

                    {realSalesChannels.length === 0 ? (
                        <NoStoreContainer>
                            <NoStoreTitle>{T._('No online store connected yet', 'Admin')}</NoStoreTitle>
                            <NoStoreDescription>
                                {T._('To import products from your store, you need to connect an online store first.', 'Admin')}
                            </NoStoreDescription>
                            <ContinueButton onClick={() => navigate(backofficeUrl('/connect-shop'))}>{T._('Connect a store', 'Admin')}</ContinueButton>
                        </NoStoreContainer>
                    ) : (
                        <>
                            {realSalesChannels.length > 1 && (
                                <FormControl>
                                    <ChannelLabel>{T._('Online Stores', 'Admin')}</ChannelLabel>
                                    <AdvancedSelect value={selectedChannelId} onChange={(value: string) => setSelectedChannelId(value)}>
                                        {realSalesChannels.map((sc) => (
                                            <option key={sc.id} value={sc.id}>
                                                {getChannelLabel(sc)}
                                            </option>
                                        ))}
                                    </AdvancedSelect>
                                </FormControl>
                            )}

                            <ContinueButtonContainer>
                                <ContinueButton onClick={handleContinue}>{T._('Continue with Store product', 'Admin')}</ContinueButton>
                            </ContinueButtonContainer>
                        </>
                    )}
                </TabPanel>

                {/* From Scratch */}
                {noEcommerceSalesChannel && (
                    <TabPanel>
                        <TabDescriptionBox $variant='standalone'>
                            {T._(
                                'Create a customizable product without connecting a store. Use it for manual orders by sending the customizer link directly to your customers, build Zakeke-hosted mini-sites, or run tests and demos. You can connect it to a store later at any time.',
                                'Admin'
                            )}
                        </TabDescriptionBox>
                        <ContinueButtonContainer>
                            <StandaloneButton onClick={handleContinue}>{T._('Continue from scratch', 'Admin')}</StandaloneButton>
                        </ContinueButtonContainer>
                    </TabPanel>
                )}

                {/* POD */}
                {showPodTab && (
                    <TabPanel>
                        <TabDescriptionBox $variant='pod'>
                            {T._(
                                'Pick a blank product from a Print on Demand service catalog, such as Printful or CustomCat. Publish it as a customizable product at your online store — customers access the customizer, personalize the product, and place their order. The POD provider takes care of printing, fulfillment, and shipping on your behalf.',
                                'Admin'
                            )}
                        </TabDescriptionBox>

                        <PodWarningBanner
                            type='WARNING'
                            variant='slim'
                            title={T._('Please note:', 'Admin')}
                            content={T._(
                                'Requires a connected online store. Natively supported platforms: Shopify, WooCommerce, and BigCommerce. Available POD providers: Printful, CustomCat, and Printeers. Other POD services can be used without native integration.',
                                'Admin'
                            )}
                        />

                        {realSalesChannels.length === 0 ? (
                            <NoStoreContainer>
                                <NoStoreTitle>{T._('No online store connected yet', 'Admin')}</NoStoreTitle>
                                <NoStoreDescription>{T._('To use a POD service, you need to connect an online store first.', 'Admin')}</NoStoreDescription>
                                <ContinueButton onClick={() => navigate(backofficeUrl('/connect-shop'))}>{T._('Connect a store', 'Admin')}</ContinueButton>
                            </NoStoreContainer>
                        ) : (
                            <>
                                {realSalesChannels.length > 1 && (
                                    <FormControl>
                                        <ChannelLabel>{T._('Online Stores', 'Admin')}</ChannelLabel>
                                        <AdvancedSelect value={podSelectedChannelId} onChange={(value: string) => setPodSelectedChannelId(value)}>
                                            {realSalesChannels.map((sc) => (
                                                <option key={sc.id} value={sc.id}>
                                                    {getChannelLabel(sc)}
                                                </option>
                                            ))}
                                        </AdvancedSelect>
                                    </FormControl>
                                )}

                                {isPodAvailableForSelected ? (
                                    <ContinueButtonContainer>
                                        <ContinueButton onClick={handleContinue}>{T._('Continue with POD service', 'Admin')}</ContinueButton>
                                    </ContinueButtonContainer>
                                ) : (
                                    <PodMismatchBanner
                                        type='WARNING'
                                        variant='slim'
                                        content={T._(
                                            'The POD option is available only for your master integration (%1). Please select the master channel to continue.',
                                            'Admin'
                                        ).replace(/%1/g, masterEcommerce.replace(/_/g, ' '))}
                                    />
                                )}
                            </>
                        )}
                    </TabPanel>
                )}
            </CenteredTabs>

            {/* --- FAQ --- */}
            <FaqSection>
                <FaqTitle>{T._('Frequently asked questions', 'Admin')}</FaqTitle>
                <FaqList>
                    {faqs.map((faq) => (
                        <FaqItem
                            key={faq.question}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openFaq === faq.question}
                            onToggle={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                        />
                    ))}
                </FaqList>
            </FaqSection>
        </PageContainer>
    );
};

export default AddProduct;
